import axios from 'axios'
import { buildColorExpression, buildStepColorExpression, buildThresholdStepExpression, buildDemographicsQueryUrl, buildHeatMapLayerQueryUrl, computeMinMax, getLayerForZoom, heatMapLayers } from './heatmap-colors'

const BASEMAP_STYLES = {
  streets: 'arcgis/streets',
  navigation: 'arcgis/navigation',
  topographic: 'arcgis/topographic',
  'light-gray': 'arcgis/light-gray',
  'dark-gray': 'arcgis/dark-gray',
  imagery: 'arcgis/imagery',
  osm: 'osm/standard'
}

const DEMOGRAPHICS_SOURCE_ID = 'esri-demographics'
const DEMOGRAPHICS_FILL_LAYER = 'demographics-fill'
const DEMOGRAPHICS_LINE_LAYER = 'demographics-outline'

// Track popup-related listeners attached per feature-layer id so removeFeatureLayer
// can detach them cleanly. Also tracks the currently visible layer popup so we can
// dismiss it when its source layer goes away.
const layerPopupListeners = new Map()
let activeLayerPopup = null

// A sub-layer group puts one fetch per active sub-layer in flight on every pan,
// and the NTAD transit host degrades badly under that: with all five transit
// sub-layers firing at once a single heavy query (Bus is 61k routes nationally)
// pushes its siblings past the proxy's 30s timeout, so layers that are fast on
// their own start returning 502s. Capping in-flight fetches trades a little
// wall-clock for every layer actually arriving.
const MAX_CONCURRENT_LAYER_FETCHES = 2
let activeLayerFetches = 0
const layerFetchQueue = []

function acquireLayerFetchSlot() {
  if (activeLayerFetches < MAX_CONCURRENT_LAYER_FETCHES) {
    activeLayerFetches++
    return Promise.resolve()
  }
  return new Promise((resolve) => { layerFetchQueue.push(resolve) })
}

function releaseLayerFetchSlot() {
  // Hand the slot straight to the next waiter rather than decrementing, so the
  // count can't drift if a queued fetch starts before another one finishes.
  const next = layerFetchQueue.shift()
  if (next) { next() } else { activeLayerFetches-- }
}

function escapeHtml(value) {
  if (value == null) { return '' }
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildLayerPopupHtml(formatted) {
  const lines = (formatted.lines || [])
    .filter(Boolean)
    .map(line => `<div class="layer-popup-line">${escapeHtml(line)}</div>`)
    .join('')
  return [
    '<div class="layer-popup">',
    formatted.type ? `<div class="layer-popup-type">${escapeHtml(formatted.type)}</div>` : '',
    formatted.name ? `<div class="layer-popup-name">${escapeHtml(formatted.name)}</div>` : '',
    lines,
    '</div>'
  ].join('')
}

/**
 * Register popup handlers for a feature layer. Two modes:
 *  - click (default): popup opens on click, anchored at the feature coordinate
 *    (points) or click point (polygons), and stays until dismissed.
 *  - hover (opts.hover): popup follows the cursor while over the layer and
 *    disappears on mouse-out — used by NMTC.
 * Handlers are tracked in layerPopupListeners keyed by the layer's stable id; the
 * stored `detach()` removes listeners and any open popup (called by removeFeatureLayer).
 * @param {Object} map - MapLibre map instance
 * @param {string} layerId - Stable layer id (the key in layerPopupListeners)
 * @param {string} eventLayerId - Layer id events bind to (layerId for points, `${layerId}-fill` for polygons)
 * @param {Function} formatter - popupFormatter(properties) => { type, name, lines[] }
 * @param {Object} [opts] - { hover: boolean }
 */
function registerLayerPopup(map, layerId, eventLayerId, formatter, opts = {}) {
  if (!formatter || layerPopupListeners.has(layerId)) { return }

  if (opts.hover) {
    let hoverPopup = null
    const onMove = (e) => {
      const feature = e.features && e.features[0]
      if (!feature) { return }
      let formatted
      try {
        formatted = formatter(feature.properties || {})
      } catch (fmtErr) {
        console.warn(`popupFormatter for ${layerId} threw:`, fmtErr && fmtErr.message)
        return
      }
      if (!formatted) { return }
      map.getCanvas().style.cursor = 'pointer'
      if (!hoverPopup) {
        hoverPopup = new window.maplibregl.Popup({ offset: 14, closeButton: false, closeOnClick: false, className: 'layer-popup-wrap' })
      }
      hoverPopup.setLngLat(e.lngLat).setHTML(buildLayerPopupHtml(formatted)).addTo(map)
    }
    const onLeave = () => {
      map.getCanvas().style.cursor = ''
      if (hoverPopup) { hoverPopup.remove(); hoverPopup = null }
    }
    map.on('mousemove', eventLayerId, onMove)
    map.on('mouseleave', eventLayerId, onLeave)
    layerPopupListeners.set(layerId, {
      detach: () => {
        map.off('mousemove', eventLayerId, onMove)
        map.off('mouseleave', eventLayerId, onLeave)
        if (hoverPopup) { hoverPopup.remove(); hoverPopup = null }
      }
    })
    return
  }

  const onClick = (e) => {
    const feature = e.features && e.features[0]
    if (!feature) { return }
    let formatted
    try {
      formatted = formatter(feature.properties || {})
    } catch (fmtErr) {
      console.warn(`popupFormatter for ${layerId} threw:`, fmtErr && fmtErr.message)
      return
    }
    if (!formatted) { return }
    const geom = feature.geometry
    const coords = (geom && geom.type === 'Point' && geom.coordinates) || [e.lngLat.lng, e.lngLat.lat]
    if (activeLayerPopup) { activeLayerPopup.remove() }
    activeLayerPopup = new window.maplibregl.Popup({ offset: 14, closeButton: true, closeOnClick: true, className: 'layer-popup-wrap' })
      .setLngLat(coords)
      .setHTML(buildLayerPopupHtml(formatted))
      .addTo(map)
    activeLayerPopup.on('close', () => { activeLayerPopup = null })
  }
  const onEnter = () => { map.getCanvas().style.cursor = 'pointer' }
  const onLeave = () => { map.getCanvas().style.cursor = '' }
  map.on('click', eventLayerId, onClick)
  map.on('mouseenter', eventLayerId, onEnter)
  map.on('mouseleave', eventLayerId, onLeave)
  layerPopupListeners.set(layerId, {
    detach: () => {
      map.off('click', eventLayerId, onClick)
      map.off('mouseenter', eventLayerId, onEnter)
      map.off('mouseleave', eventLayerId, onLeave)
    }
  })
}

const MAPLIBRE_VERSION = '5.22.0'
const MAPLIBRE_JS_SRI = 'sha384-U054LTKiMIJKEecR8PKFiUZdvkGWHjfPBnen5hSmR9TwfOfgZmKbskC8Rs9dCm/1'
const MAPLIBRE_CSS_SRI = 'sha384-MGCxhspF/+ufueUgol3FDkiAYQbpSNRhBT0VWHJt64U8qIy9qlnXWx8LAbj6niPH'

const CDN_URLS = {
  primary: {
    js: `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`,
    css: `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`
  },
  fallback: {
    js: `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`,
    css: `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`
  }
}

let _loadPromise = null

function loadScript(src, integrity) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    if (integrity) {
      script.integrity = integrity
      script.crossOrigin = 'anonymous'
    }
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

function loadMapLibre() {
  if (window.maplibregl) {
    return Promise.resolve(window.maplibregl)
  }
  if (!_loadPromise) {
    _loadPromise = loadScript(CDN_URLS.primary.js, MAPLIBRE_JS_SRI)
      .catch(() => {
        // Fallback to jsDelivr if unpkg fails
        console.warn('MapLibre primary CDN failed, trying fallback...')
        return loadScript(CDN_URLS.fallback.js)
      })
      .then(() => {
        if (!window.maplibregl) {
          throw new Error('MapLibre GL JS failed to initialize')
        }
        return window.maplibregl
      })
      .catch((err) => {
        _loadPromise = null
        throw err
      })
  }
  return _loadPromise
}

export default function ({ $config, $axios }, inject) {
  // Returns the current Authorization header value from axios defaults
  function getAuthHeader() {
    return ($axios.defaults && $axios.defaults.headers && $axios.defaults.headers.common &&
      $axios.defaults.headers.common.Authorization) || ''
  }

  // Routes ArcGIS requests through our server-side proxy so the API token
  // never leaves the backend. Called by MapLibre for every resource fetch.
  // Uses absolute URLs because MapLibre's fetch() API rejects relative paths.
  function arcgisTransformRequest(url, resourceType) {
    const origin = window.location.origin
    const headers = {}
    const auth = getAuthHeader()
    if (auth) {
      headers.Authorization = auth
    }

    if (url && typeof url === 'string' && url.includes('.arcgis.com')) {
      return {
        url: `${origin}/api/v1/arcgis/proxy?url=${encodeURIComponent(url)}`,
        headers
      }
    }

    return { url }
  }

  const MapLibreService = {
    get maplibregl() { return window.maplibregl },
    BASEMAP_STYLES,
    loadMapLibre,

    async fetchStyle(style = 'arcgis/streets') {
      const stylePath = BASEMAP_STYLES[style] || style
      return await $axios.$get(`/arcgis/style/${stylePath}`)
    },

    async createMap(container, options = {}) {
      const ml = await loadMapLibre()
      const style = options.style || 'streets'
      delete options.style
      const styleJson = await this.fetchStyle(style)

      // Merge preserveDrawingBuffer into canvasContextAttributes (MapLibre v5 API)
      const canvasCtx = { antialias: true }
      if (options.preserveDrawingBuffer) {
        canvasCtx.preserveDrawingBuffer = true
        delete options.preserveDrawingBuffer
      }

      return new ml.Map({
        container,
        style: styleJson,
        center: options.center || [-98.5795, 39.8283], // US center default
        zoom: options.zoom || 4,
        minZoom: options.minZoom !== undefined ? options.minZoom : 2,
        attributionControl: options.attributionControl !== undefined ? options.attributionControl : true,
        transformRequest: arcgisTransformRequest,
        canvasContextAttributes: canvasCtx,
        ...options
      })
    },

    enable3D(map) {
      if (!map) { return }
      // Guard: skip if 3D is already active
      if (map.getTerrain() && map.getPitch() > 0) { return }

      // 1. Terrain elevation
      if (!map.getSource('terrain-dem')) {
        map.addSource('terrain-dem', {
          type: 'raster-dem',
          tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
          encoding: 'terrarium',
          tileSize: 256,
          maxzoom: 15
        })
      }
      map.setTerrain({ source: 'terrain-dem', exaggeration: 2 })

      // 2. 3D buildings from OpenFreeMap (free, no API key)
      if (!map.getLayer('3d-buildings')) {
        // Insert buildings beneath first label layer
        const layers = map.getStyle().layers || []
        let labelLayerId
        for (let i = 0; i < layers.length; i++) {
          if (layers[i].type === 'symbol' && layers[i].layout && layers[i].layout['text-field']) {
            labelLayerId = layers[i].id
            break
          }
        }

        if (!map.getSource('openfreemap')) {
          map.addSource('openfreemap', {
            url: 'https://tiles.openfreemap.org/planet',
            type: 'vector'
          })
        }

        map.addLayer(
          {
            id: '3d-buildings',
            source: 'openfreemap',
            'source-layer': 'building',
            type: 'fill-extrusion',
            minzoom: 15,
            filter: ['!=', ['get', 'hide_3d'], true],
            paint: {
              'fill-extrusion-color': [
                'interpolate', ['linear'], ['get', 'render_height'],
                0, 'lightgray',
                200, 'royalblue',
                400, 'lightblue'
              ],
              'fill-extrusion-height': [
                'interpolate', ['linear'], ['zoom'],
                15, 0,
                16, ['get', 'render_height']
              ],
              'fill-extrusion-base': [
                'case',
                ['>=', ['get', 'zoom'], 16],
                ['get', 'render_min_height'],
                0
              ]
            }
          },
          labelLayerId
        )
      }

      // 3. Pitch camera for 3D perspective
      map.easeTo({ pitch: 60, duration: 1000 })
    },

    disable3D(map) {
      if (!map) { return }
      map.setTerrain(null)
      if (map.getLayer('3d-buildings')) {
        map.removeLayer('3d-buildings')
      }
      if (map.getSource('openfreemap')) {
        map.removeSource('openfreemap')
      }
      map.easeTo({ pitch: 0, duration: 1000 })
    },

    createMarkerElement(label, color = '#403A60') {
      // Validate color to prevent SVG injection
      const safeColor = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color) ? color : '#403A60'
      const el = document.createElement('div')
      el.className = 'maplibre-custom-marker'
      el.style.cursor = 'pointer'
      el.innerHTML = `
        <svg width="30" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12,2C8.1,2,5,5.1,5,9c0,5.2,7,13,7,13s7-7.8,7-13C19,5.1,15.9,2,12,2z"
                fill="${safeColor}" stroke="none"/>
          <text x="12" y="12" text-anchor="middle" dominant-baseline="central"
                fill="white" font-size="8" font-weight="bold" font-family="sans-serif">${label}</text>
        </svg>
      `
      return el
    },

    /**
     * Fetch demographics GeoJSON for the current map viewport.
     * Uses caching and AbortController to avoid redundant/stale requests.
     * @param {Object} map - MapLibre map instance
     * @param {string} field - ESRI field name
     * @param {Object} [options] - { signal: AbortSignal }
     * @returns {Promise<{geojson: Object, min: number, max: number, layerName: string}>}
     */
    async fetchDemographics(map, field, options = {}) {
      // Cancel any in-flight demographics request
      if (this._demographicsCancelSource) {
        this._demographicsCancelSource.cancel('new request')
      }
      const cancelSource = axios.CancelToken.source()
      this._demographicsCancelSource = cancelSource

      const b = map.getBounds()
      const zoom = map.getZoom()
      const bounds = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth()
      }
      const layerConfig = getLayerForZoom(zoom)
      const queryUrl = buildDemographicsQueryUrl(bounds, field, layerConfig, zoom)

      // Check cache (keyed by URL which encodes bounds + field + layer)
      if (!this._demographicsCache) { this._demographicsCache = new Map() }
      const cached = this._demographicsCache.get(queryUrl)
      if (cached) {
        return { ...cached, layerName: layerConfig.name }
      }

      // Route through node-api for shared 24h server-side caching
      const proxyUrl = `/demographics/layer?url=${encodeURIComponent(queryUrl)}`
      let geojson
      try {
        geojson = await $axios.$get(proxyUrl, { cancelToken: cancelSource.token })
      } catch (err) {
        if (axios.isCancel(err)) {
          return { geojson: null, min: 0, max: 0, cancelled: true }
        }
        throw err
      }
      // Handle string responses (proxy streams raw response which may not be parsed)
      if (typeof geojson === 'string') {
        try {
          geojson = JSON.parse(geojson)
        } catch (e) {
          console.error('Failed to parse demographics response:', e)
          return { geojson: null, min: 0, max: 0 }
        }
      }
      const { min, max } = computeMinMax(geojson, field)
      const result = { geojson, min, max }

      // Cache the result (limit cache size to 20 entries)
      if (this._demographicsCache.size > 20) {
        const firstKey = this._demographicsCache.keys().next().value
        this._demographicsCache.delete(firstKey)
      }
      this._demographicsCache.set(queryUrl, result)

      return { ...result, layerName: layerConfig.name }
    },

    /**
     * Fetch a static heat map layer (e.g. seismic hazards) for the viewport.
     * @param {Object} map - MapLibre map instance
     * @param {string} layerKey - Key from heatMapLayers (e.g. 'seismichazards')
     * @returns {Promise<{geojson: Object, min: number, max: number, layerDef: Object}>}
     */
    async fetchHeatMapLayer(map, layerKey) {
      if (this._demographicsCancelSource) {
        this._demographicsCancelSource.cancel('new request')
      }
      const cancelSource = axios.CancelToken.source()
      this._demographicsCancelSource = cancelSource

      const layerDef = heatMapLayers[layerKey]
      if (!layerDef) {
        return { geojson: null, min: 0, max: 0 }
      }
      const b = map.getBounds()
      const zoom = map.getZoom()
      const bounds = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth()
      }
      const queryUrl = buildHeatMapLayerQueryUrl(bounds, layerDef, zoom)

      if (!this._demographicsCache) { this._demographicsCache = new Map() }
      const cached = this._demographicsCache.get(queryUrl)
      if (cached) {
        return { ...cached, layerDef }
      }

      // Route through node-api for shared 24h server-side caching
      const proxyUrl = `/demographics/layer?url=${encodeURIComponent(queryUrl)}`
      let geojson
      try {
        geojson = await $axios.$get(proxyUrl, { cancelToken: cancelSource.token })
      } catch (err) {
        if (axios.isCancel(err)) {
          return { geojson: null, min: 0, max: 0, cancelled: true }
        }
        throw err
      }
      if (typeof geojson === 'string') {
        try {
          geojson = JSON.parse(geojson)
        } catch (e) {
          console.error('Failed to parse heat map layer response:', e)
          return { geojson: null, min: 0, max: 0 }
        }
      }
      const valueField = layerDef.fields.find(f => f !== 'OBJECTID') || layerDef.fields[0]
      const { min, max } = computeMinMax(geojson, valueField)
      const result = { geojson, min, max }

      if (this._demographicsCache.size > 20) {
        const firstKey = this._demographicsCache.keys().next().value
        this._demographicsCache.delete(firstKey)
      }
      this._demographicsCache.set(queryUrl, result)

      return { ...result, layerDef }
    },

    /**
     * Clear the demographics data cache.
     */
    clearDemographicsCache() {
      if (this._demographicsCache) {
        this._demographicsCache.clear()
      }
    },

    /**
     * Fetch a "computed" heat map layer (e.g. broadband) for the viewport and
     * derive its value field client-side. Unlike fetchHeatMapLayer, the coloring
     * field does not exist in the raw response — it is computed per feature here.
     * Features with no denominator (TotalBSLs <= 0) are dropped so they render blank.
     * @param {Object} map - MapLibre map instance
     * @param {string} layerKey - Key from heatMapLayers (e.g. 'broadband')
     * @returns {Promise<{geojson: Object|null, layerDef: Object, cancelled?: boolean}>}
     */
    async fetchComputedHeatMapLayer(map, layerKey) {
      if (this._demographicsCancelSource) {
        this._demographicsCancelSource.cancel('new request')
      }
      const cancelSource = axios.CancelToken.source()
      this._demographicsCancelSource = cancelSource

      const layerDef = heatMapLayers[layerKey]
      if (!layerDef) { return { geojson: null, layerDef: null } }

      const b = map.getBounds()
      const bounds = { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() }
      const queryUrl = buildHeatMapLayerQueryUrl(bounds, layerDef, map.getZoom())
      const proxyUrl = `/demographics/layer?url=${encodeURIComponent(queryUrl)}`

      let geojson
      try {
        geojson = await $axios.$get(proxyUrl, { cancelToken: cancelSource.token })
      } catch (err) {
        if (axios.isCancel(err)) { return { geojson: null, layerDef, cancelled: true } }
        throw err
      }
      if (typeof geojson === 'string') {
        try { geojson = JSON.parse(geojson) } catch (e) { return { geojson: null, layerDef } }
      }
      if (!geojson || !geojson.features) { return { geojson: null, layerDef } }
      if (geojson.crs) { delete geojson.crs }

      // Compute pctServed / pctFiber; drop counties with no broadband-serviceable locations.
      geojson.features = geojson.features.filter((f) => {
        const p = f.properties || {}
        const total = Number(p.TotalBSLs)
        if (!total || total <= 0) { return false }
        p.pctServed = Math.round((Number(p.ServedBSLs) || 0) / total * 100)
        p.pctFiber = Math.round((Number(p.ServedBSLsFiber) || 0) / total * 100)
        return true
      })

      return { geojson, layerDef }
    },

    /**
     * Add or update the choropleth layer on the map.
     * @param {Object} map - MapLibre map instance
     * @param {Object} geojson - GeoJSON FeatureCollection
     * @param {string} field - ESRI field name
     * @param {string} colorScale - Color scale key from HEATMAP_COLOR_SCALES
     * @param {number} min - Minimum value for color interpolation
     * @param {number} max - Maximum value for color interpolation
     */
    addChoroplethLayer(map, geojson, field, colorScale, min, max) {
      const colorExpr = buildColorExpression(field, min, max, colorScale)

      // Strip non-standard "crs" field that ESRI adds (not in RFC 7946)
      if (geojson.crs) {
        delete geojson.crs
      }

      // Find the first symbol/marker layer to insert below
      const layers = map.getStyle().layers || []
      let beforeLayerId
      for (const layer of layers) {
        if (layer.type === 'symbol' || layer.id === '3d-buildings') {
          beforeLayerId = layer.id
          break
        }
      }

      try {
        if (map.getSource(DEMOGRAPHICS_SOURCE_ID)) {
          // Update existing source data
          map.getSource(DEMOGRAPHICS_SOURCE_ID).setData(geojson)
          // Update paint properties
          if (map.getLayer(DEMOGRAPHICS_FILL_LAYER)) {
            map.setPaintProperty(DEMOGRAPHICS_FILL_LAYER, 'fill-color', colorExpr)
          }
        } else {
          // Add new source and layers
          map.addSource(DEMOGRAPHICS_SOURCE_ID, {
            type: 'geojson',
            data: geojson
          })

          map.addLayer({
            id: DEMOGRAPHICS_FILL_LAYER,
            type: 'fill',
            source: DEMOGRAPHICS_SOURCE_ID,
            paint: {
              'fill-color': colorExpr,
              'fill-opacity': 0.55
            }
          }, beforeLayerId)

          map.addLayer({
            id: DEMOGRAPHICS_LINE_LAYER,
            type: 'line',
            source: DEMOGRAPHICS_SOURCE_ID,
            paint: {
              'line-color': 'rgba(80, 80, 80, 0.4)',
              'line-width': 0.5
            }
          }, beforeLayerId)
        }
      } catch (err) {
        console.error('Failed to add/update choropleth layer:', err)
      }
    },

    /**
     * Add or update the choropleth layer using a prebuilt MapLibre color
     * expression (used by computed/classed heat maps like broadband). Reuses the
     * same source/layer ids as addChoroplethLayer so clear/restore paths work.
     * @param {Object} map - MapLibre map instance
     * @param {Object} geojson - GeoJSON FeatureCollection (value field already computed)
     * @param {Array} colorExpr - MapLibre expression for 'fill-color'
     */
    addChoroplethLayerExpr(map, geojson, colorExpr) {
      if (geojson.crs) { delete geojson.crs }

      const layers = map.getStyle().layers || []
      let beforeLayerId
      for (const layer of layers) {
        if (layer.type === 'symbol' || layer.id === '3d-buildings') {
          beforeLayerId = layer.id
          break
        }
      }

      try {
        if (map.getSource(DEMOGRAPHICS_SOURCE_ID)) {
          map.getSource(DEMOGRAPHICS_SOURCE_ID).setData(geojson)
          if (map.getLayer(DEMOGRAPHICS_FILL_LAYER)) {
            map.setPaintProperty(DEMOGRAPHICS_FILL_LAYER, 'fill-color', colorExpr)
          }
        } else {
          map.addSource(DEMOGRAPHICS_SOURCE_ID, { type: 'geojson', data: geojson })
          map.addLayer({
            id: DEMOGRAPHICS_FILL_LAYER,
            type: 'fill',
            source: DEMOGRAPHICS_SOURCE_ID,
            paint: { 'fill-color': colorExpr, 'fill-opacity': 0.55 }
          }, beforeLayerId)
          map.addLayer({
            id: DEMOGRAPHICS_LINE_LAYER,
            type: 'line',
            source: DEMOGRAPHICS_SOURCE_ID,
            paint: { 'line-color': 'rgba(80, 80, 80, 0.4)', 'line-width': 0.5 }
          }, beforeLayerId)
        }
      } catch (err) {
        console.error('Failed to add/update computed choropleth layer:', err)
      }
    },

    /**
     * Remove the choropleth layer and source from the map.
     * @param {Object} map - MapLibre map instance
     */
    removeChoroplethLayer(map) {
      if (!map) { return }
      if (map.getLayer(DEMOGRAPHICS_FILL_LAYER)) {
        map.removeLayer(DEMOGRAPHICS_FILL_LAYER)
      }
      if (map.getLayer(DEMOGRAPHICS_LINE_LAYER)) {
        map.removeLayer(DEMOGRAPHICS_LINE_LAYER)
      }
      if (map.getSource(DEMOGRAPHICS_SOURCE_ID)) {
        map.removeSource(DEMOGRAPHICS_SOURCE_ID)
      }
    },

    /**
     * Check if choropleth layers exist on the map.
     */
    hasChoroplethLayer(map) {
      return map && !!map.getLayer(DEMOGRAPHICS_FILL_LAYER)
    },

    /**
     * Build an ESRI FeatureServer /query URL with a bounding-box spatial filter.
     * @param {string} baseUrl - The FeatureServer layer URL (without /query suffix)
     * @param {Object} bounds - MapLibre LngLatBounds (must support .getWest/getSouth/getEast/getNorth)
     * @param {string} [where] - Optional WHERE clause (defaults to no filter)
     * @param {number} [limit] - Max records to return (default 1000)
     */
    buildFeatureQueryUrl(baseUrl, bounds, where = '1=1', limit = 1000) {
      // Use comma-separated bbox instead of JSON-encoded geometry: TIGERweb's WAF
      // rejects requests containing JSON `{"xmin":...}` shapes outright. The
      // comma form is accepted by every ESRI service we hit (TIGERweb, FiaPA4ga,
      // P3ePLMYs, QVENGdaPbd4, etc.).
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`
      const params = new URLSearchParams({
        geometry: bbox,
        geometryType: 'esriGeometryEnvelope',
        inSR: '4326',
        spatialRel: 'esriSpatialRelIntersects',
        outFields: '*',
        f: 'geojson',
        // Trim coordinate precision and let the server simplify geometry —
        // turns USA_ZIP_Codes from a 36 MB / 40 s response into 1.3 MB / 1 s,
        // and similarly helps Opportunity Zones, Census Tracts, Critical Habitats.
        geometryPrecision: '4',
        maxAllowableOffset: '0.005',
        resultRecordCount: String(limit)
      })
      if (where && where !== '1=1') {
        params.set('where', where)
      }
      return `${baseUrl}/query?${params.toString()}`
    },

    /**
     * Fetch GeoJSON from an ESRI FeatureServer (via the /demographics/layer proxy)
     * and add or refresh it as a source + layer on the map.
     * Supports point, line, and polygon geometries. Polygon layers create a
     * `${layerId}-fill` and `${layerId}-outline` layer pair.
     * If `options.staticGeoJson` is provided, no fetch is performed.
     * @param {Object} map - MapLibre map instance
     * @param {string} layerId - Stable id for the source/layer
     * @param {string|null} esriUrl - FeatureServer URL (null when using staticGeoJson)
     * @param {string} geomType - 'point' | 'line' | 'polygon'
     * @param {string} color - Hex color for paint
     * @param {Object} [options] - { where, staticGeoJson, limit }
     */
    async addFeatureLayer(map, layerId, esriUrl, geomType, color, options = {}) {
      if (!map) { return }
      let geojson
      if (options.staticGeoJson) {
        geojson = options.staticGeoJson
      } else if (esriUrl) {
        const bounds = map.getBounds()
        const queryUrl = this.buildFeatureQueryUrl(esriUrl, bounds, options.where, options.limit)
        const proxyUrl = `/demographics/layer?url=${encodeURIComponent(queryUrl)}`
        await acquireLayerFetchSlot()
        try {
          geojson = await $axios.$get(proxyUrl)
        } catch (err) {
          // The NTAD host intermittently times out on queries that succeed when
          // repeated verbatim, so retry once before giving up on the layer. The
          // fetch slot is deliberately held across both attempts so a retry
          // queues with everything else instead of adding load on a host that is
          // already struggling.
          try {
            geojson = await $axios.$get(proxyUrl)
          } catch (retryErr) {
            console.error(`Failed to fetch layer ${layerId}:`, retryErr && retryErr.message)
            return
          }
        } finally {
          releaseLayerFetchSlot()
        }
        if (typeof geojson === 'string') {
          try { geojson = JSON.parse(geojson) } catch (_) { return }
        }
      } else {
        return
      }
      if (!geojson || !geojson.features) { return }
      if (geojson.crs) { delete geojson.crs }

      // Reuse existing source if present (viewport refresh path)
      if (map.getSource(layerId)) {
        map.getSource(layerId).setData(geojson)
        return
      }

      // Insert below first symbol layer so labels stay readable
      const layers = map.getStyle().layers || []
      let beforeLayerId
      for (const layer of layers) {
        if (layer.type === 'symbol' || layer.id === '3d-buildings') {
          beforeLayerId = layer.id
          break
        }
      }

      try {
        map.addSource(layerId, { type: 'geojson', data: geojson })

        if (geomType === 'point') {
          // If an icon is provided, try to use a symbol layer with the image
          let usedIcon = false
          if (options.icon) {
            const imageId = `layer-icon-${layerId}`
            try {
              if (!map.hasImage(imageId)) {
                // Sub-layers share a parent SVG but render in different colors.
                // Fetch the SVG, swap its #000000 fill for the requested color, and
                // hand the canvas a data-URI so each sub-layer gets its own tint.
                let iconSrc = options.icon
                if (options.iconColor && /\.svg($|\?)/i.test(options.icon)) {
                  const res = await fetch(options.icon)
                  const svgText = await res.text()
                  const tinted = svgText.replace(/#000000/gi, options.iconColor)
                  iconSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tinted)}`
                }
                const img = await new Promise((resolve, reject) => {
                  const el = new Image()
                  el.onload = () => resolve(el)
                  el.onerror = reject
                  el.src = iconSrc
                })
                // Normalise to 64×64 so all icons render at a consistent size
                const SIZE = 64
                const canvas = document.createElement('canvas')
                canvas.width = SIZE
                canvas.height = SIZE
                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0, SIZE, SIZE)
                map.addImage(imageId, ctx.getImageData(0, 0, SIZE, SIZE), { pixelRatio: 2 })
              }
              map.addLayer({
                id: layerId,
                type: 'symbol',
                source: layerId,
                layout: {
                  'icon-image': imageId,
                  'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 10, 1.0],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true
                }
              }, beforeLayerId)
              usedIcon = true
            } catch (iconErr) {
              console.warn(`Could not load layer icon ${options.icon}, falling back to circle:`, iconErr && iconErr.message)
            }
          }

          if (!usedIcon) {
            map.addLayer({
              id: layerId,
              type: 'circle',
              source: layerId,
              paint: {
                'circle-color': color,
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 4, 10, 7],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1,
                'circle-opacity': 0.9
              }
            }, beforeLayerId)
          }

          // Click-to-popup for icon/circle features. Only register once per
          // layerId — the moveend re-fetch path reuses an existing source and
          // returns early before reaching here.
          registerLayerPopup(map, layerId, layerId, options.popupFormatter)
        } else if (geomType === 'line') {
          map.addLayer({
            id: layerId,
            type: 'line',
            source: layerId,
            paint: {
              'line-color': color,
              'line-width': 1.5,
              'line-opacity': 0.85
            }
          }, beforeLayerId)

          // A 1.5px line is all but unclickable, so line layers that carry a
          // popup get a transparent wider line on top as the hit target.
          if (options.popupFormatter) {
            map.addLayer({
              id: `${layerId}-hit`,
              type: 'line',
              source: layerId,
              paint: {
                'line-color': color,
                'line-width': 12,
                'line-opacity': 0
              }
            }, beforeLayerId)
            registerLayerPopup(map, layerId, `${layerId}-hit`, options.popupFormatter)
          }
        } else {
          // polygon — fill + outline. options.outlineColor / fillOpacity /
          // outlineOpacity let a layer override the defaults (e.g. NMTC's flat
          // style with an 8-digit hex fill that carries its own alpha).
          map.addLayer({
            id: `${layerId}-fill`,
            type: 'fill',
            source: layerId,
            paint: {
              'fill-color': color,
              'fill-opacity': options.fillOpacity != null ? options.fillOpacity : 0.25
            }
          }, beforeLayerId)
          map.addLayer({
            id: `${layerId}-outline`,
            type: 'line',
            source: layerId,
            paint: {
              'line-color': options.outlineColor || color,
              'line-width': 1,
              'line-opacity': options.outlineOpacity != null ? options.outlineOpacity : 0.7
            }
          }, beforeLayerId)

          // Popup for polygons, bound to the fill layer. options.hoverPopup makes
          // it follow the cursor (NMTC); otherwise it's click-to-open.
          registerLayerPopup(map, layerId, `${layerId}-fill`, options.popupFormatter, { hover: !!options.hoverPopup })
        }
      } catch (err) {
        console.error(`Failed to add feature layer ${layerId}:`, err && err.message)
      }
    },

    /**
     * Remove a feature layer added via addFeatureLayer.
     * @param {Object} map - MapLibre map instance
     * @param {string} layerId
     * @param {string} geomType - must match the geomType used when adding
     */
    removeFeatureLayer(map, layerId, geomType) {
      if (!map) { return }
      // Detach popup click/hover handlers and dismiss any popup still showing.
      const handlers = layerPopupListeners.get(layerId)
      if (handlers) {
        if (typeof handlers.detach === 'function') { handlers.detach() }
        layerPopupListeners.delete(layerId)
      }
      if (activeLayerPopup) {
        activeLayerPopup.remove()
        activeLayerPopup = null
      }
      const layerIds = geomType === 'polygon'
        ? [`${layerId}-fill`, `${layerId}-outline`]
        : [layerId, `${layerId}-hit`]
      layerIds.forEach((id) => {
        if (map.getLayer(id)) { map.removeLayer(id) }
      })
      if (map.getSource(layerId)) {
        map.removeSource(layerId)
      }
      // Clean up any icon image that was added for this layer
      const imageId = `layer-icon-${layerId}`
      if (map.hasImage(imageId)) {
        map.removeImage(imageId)
      }
    },

    /**
     * Add a Strata vector-tile (PBF) line layer to the map. No proxy is used —
     * tiles are fetched directly from the Strata tile CDN.
     * @param {Object} map - MapLibre map instance
     * @param {string} layerId
     * @param {string} tilePath - Full path inside STRATA_TILE_BASE, including {z}/{x}/{y}.pbf
     *                            e.g. 'us-interstates/v1/{z}/{x}/{y}.pbf' or 'amtrak/railroad/v1/{z}/{x}/{y}.pbf'
     * @param {string} sourceLayer - The source-layer name inside the PBF
     * @param {string} color
     * @param {boolean} [dashed]
     */
    addVectorTileLayer(map, layerId, tilePath, sourceLayer, color, dashed = false) {
      if (!map) { return }
      if (map.getSource(layerId)) { return }
      const STRATA = 'https://strata-tile-server.sfo3.cdn.digitaloceanspaces.com/geo'
      try {
        map.addSource(layerId, {
          type: 'vector',
          tiles: [`${STRATA}/${tilePath}`],
          maxzoom: 16
        })
        const paint = {
          'line-color': color,
          'line-width': 2,
          'line-opacity': 0.85
        }
        if (dashed) { paint['line-dasharray'] = [4, 4] }
        map.addLayer({
          id: layerId,
          type: 'line',
          source: layerId,
          'source-layer': sourceLayer,
          paint
        })
      } catch (err) {
        console.error(`Failed to add vector tile layer ${layerId}:`, err && err.message)
      }
    },

    /**
     * Remove a vector tile layer added via addVectorTileLayer.
     */
    removeVectorTileLayer(map, layerId) {
      if (!map) { return }
      if (map.getLayer(layerId)) { map.removeLayer(layerId) }
      if (map.getSource(layerId)) { map.removeSource(layerId) }
    }
  }

  inject('MapLibre', MapLibreService)
}
